// Nine Lives '84 - the cheese room.
//
// A great wedge of Swiss cheese sits on a long table and climbs the room
// in steps; the floor beneath the table is free to run along and leave
// pawprints. Mice peek out of the holes at random; touch one while it is
// out to catch it. At a hole, the action button sends Tabs crawling
// through the tunnels to come out of another hole, safe from the broom on
// the way. Catch enough mice.

import type { Cat } from '../cat';
import { CRAWL_TICKS, K_ACTION } from '../constants';
import { Room, type RoomContext } from '../room';
import type { Box, GameEvent, Platform, RoomKind } from '../types';

export interface Hole {
  /** Centre of the hole as drawn on the cheese's front face. */
  x: number;
  y: number;
  /** The spot Tabs must stand in to interact with this hole. */
  trigger: Box;
  /** Ticks a mouse stays peeking; 0 when the hole is empty. */
  mouse: number;
}

export const TABLE_TOP = 36;
const TABLE_X = 60;
const TABLE_W = 240;
const STEP_H = 26;
const STEP_X0 = 100;
const STEP_DX = 30;
const STEP_RIGHT = 300;
const STEPS = 4;
/** Width of the solid strip at the left of each step. */
const RISER_W = 12;
/** Holes along the top of the wedge. */
const TOP_HOLE_XS = [210, 250, 290];

export class CheeseRoom extends Room {
  readonly kind: RoomKind = 'cheese';
  readonly taskName = 'mice';
  readonly table: Platform = {
    kind: 'furniture',
    x: TABLE_X,
    y: TABLE_TOP - 3,
    w: TABLE_W,
    h: 3,
    oneWay: true,
  };
  /** The wedge's steps as drawn (solid-looking blocks). */
  readonly steps: Platform[] = [];
  /** What actually collides: a one-way tread per step, and a solid strip at each riser. */
  readonly treads: Platform[] = [];
  readonly risers: Platform[] = [];
  readonly holes: Hole[] = [];
  caught = 0;
  /** Ticks left crawling, 0 otherwise. */
  crawling = 0;
  private crawlFrom: Hole | null = null;
  private mouseTimer: number;

  constructor(ctx: RoomContext) {
    super(ctx);
    // Each tread is STEP_DX wide and holds two standing spots: its own hole
    // (front half) and the next riser's hole (back half). Spots never
    // overlap, and Tabs is "at" the spot his centre is in.
    const half = STEP_DX / 2;
    for (let i = 0; i < STEPS; i++) {
      const x = STEP_X0 + i * STEP_DX;
      const y = TABLE_TOP + i * STEP_H;
      const step: Platform = { kind: 'cheese', x, y, w: STEP_RIGHT - x, h: STEP_H, oneWay: false };
      this.steps.push(step);
      // Jump up through the cheese from anywhere beneath and land on a tread;
      // the riser strip stays solid so the steps still read as steps to walk into.
      this.treads.push({ kind: 'cheese', x, y: y + STEP_H - 3, w: STEP_RIGHT - x, h: 3, oneWay: true });
      this.risers.push({ kind: 'cheese', x, y, w: RISER_W, h: STEP_H - 3, oneWay: false });
      // A hole low in the riser, reached standing beside it on the surface below.
      this.holes.push({
        x: x + 6,
        y: y + 8,
        trigger: { x: x - half, y, w: half, h: 12 },
        mouse: 0,
      });
      if (i < STEPS - 1) {
        // A hole in the front of the step, stood over from its tread.
        this.holes.push({
          x: x + 7,
          y: y + STEP_H - 7,
          trigger: { x, y: y + STEP_H, w: half, h: 10 },
          mouse: 0,
        });
      }
    }
    const top = this.steps[STEPS - 1];
    for (const hx of TOP_HOLE_XS) {
      this.holes.push({
        x: hx,
        y: top.y + top.h - 7,
        trigger: { x: hx - 11, y: top.y + top.h, w: 22, h: 10 },
        mouse: 0,
      });
    }
    this.platforms.push(this.table, ...this.treads, ...this.risers);
    this.mouseTimer = ctx.rng.int(...ctx.params.roomMouseEvery);
  }

  get have(): number {
    return this.caught;
  }

  get need(): number {
    return this.ctx.params.cheeseMice;
  }

  protected override catBusy(): boolean {
    return this.crawling > 0;
  }

  /** The hole Tabs is standing at: his centre inside its spot, on its surface. */
  holeAt(cat: Cat): Hole | null {
    if (!cat.onGround) return null;
    const cx = cat.x + cat.w / 2;
    return (
      this.holes.find(
        (h) =>
          cx >= h.trigger.x &&
          cx < h.trigger.x + h.trigger.w &&
          Math.abs(cat.y - h.trigger.y) < 0.5,
      ) ?? null
    );
  }

  protected update(cat: Cat, keys: number, events: GameEvent[]): void {
    const { rng, params } = this.ctx;

    if (this.crawling > 0) {
      if (--this.crawling === 0) this.emerge(cat, events);
      return;
    }

    if (--this.mouseTimer <= 0) {
      this.mouseTimer = rng.int(...params.roomMouseEvery);
      const empty = this.holes.filter((h) => h.mouse === 0);
      if (empty.length) {
        empty[rng.int(0, empty.length - 1)].mouse = rng.int(...params.mousePeek);
      }
    }
    for (const h of this.holes) {
      if (h.mouse > 0) h.mouse--;
    }

    const hole = this.holeAt(cat);
    if (hole && hole.mouse > 0) {
      this.catch(hole, events);
      if (this.done) return;
    }
    if (hole && keys & K_ACTION) {
      this.crawling = CRAWL_TICKS;
      this.crawlFrom = hole;
      cat.vx = 0;
      cat.vy = 0;
      events.push({ kind: 'crawl' });
    }
  }

  private catch(hole: Hole, events: GameEvent[]): void {
    hole.mouse = 0;
    this.caught++;
    events.push({ kind: 'mouseCaught', have: this.caught, need: this.need });
    if (this.caught >= this.need) this.done = true;
  }

  private emerge(cat: Cat, events: GameEvent[]): void {
    const others = this.holes.filter((h) => h !== this.crawlFrom);
    const to = others[this.ctx.rng.int(0, others.length - 1)];
    cat.x = to.trigger.x + to.trigger.w / 2 - cat.w / 2;
    cat.y = to.trigger.y;
    cat.vx = 0;
    cat.vy = 0;
    cat.onGround = false;
    cat.ground = null;
    cat.knockedTicks = 0;
    this.crawlFrom = null;
    // A mouse met in the tunnel is a mouse caught.
    if (to.mouse > 0) this.catch(to, events);
  }
}
